import { motion, useReducedMotion } from 'framer-motion'
import type { Note } from '@/types'
import { SectionActionButton } from '@/store/components/SectionActionButton'
import { FragranceNoteChip } from '@/store/components/FragranceNoteChip'
import { homeEase } from './motion'
import { cn } from '@/lib/utils'

interface HomeFragranceNotesProps {
  notes: Note[]
  showViewAll?: boolean
}

export function HomeFragranceNotes({
  notes,
  showViewAll = false,
}: HomeFragranceNotesProps) {
  const reduceMotion = useReducedMotion()

  if (notes.length === 0) return null

  return (
    <section
      id="fragrance-notes"
      className="relative overflow-hidden bg-white"
      aria-labelledby="fragrance-notes-title"
    >
      <div className="pointer-events-none absolute inset-0" aria-hidden>
        <div className="absolute inset-0 bg-gradient-to-b from-[#F3EFE8] via-white to-[#FAF8F4]" />
        <div className="absolute inset-x-0 top-0 h-24 bg-[radial-gradient(ellipse_80%_100%_at_50%_0%,rgba(249,224,118,0.1),transparent)]" />
      </div>
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-gold-500/25 to-transparent" />

      <div className="store-container relative py-8 md:py-10">
        <div className="mb-5 md:mb-6">
          <p className="type-eyebrow text-[11px] md:text-xs">Discover</p>
          <div className="flex items-center justify-between gap-3 sm:gap-4">
            <h2
              id="fragrance-notes-title"
              className="min-w-0 flex-1 font-display text-xl font-bold tracking-tight gold-gradient-text sm:text-2xl md:text-3xl lg:text-4xl"
            >
              Choose by Fragrance notes
            </h2>
            {showViewAll ? (
              <SectionActionButton to="/notes" animateIn={false}>
                View all
              </SectionActionButton>
            ) : null}
          </div>
        </div>

        <div className="relative sm:-mx-6 lg:-mx-8">
          <div
            className={cn(
              'flex gap-4 overflow-x-auto pb-1 pt-0.5 sm:gap-5 md:gap-6 sm:px-6 lg:px-8',
              'snap-x snap-mandatory scroll-smooth',
              '[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden'
            )}
          >
            {notes.map((note, index) => (
              <motion.div
                key={note.id}
                initial={reduceMotion ? false : { opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.4,
                  ease: homeEase,
                  delay: index * 0.04,
                }}
                className="shrink-0 snap-start"
              >
                <FragranceNoteChip
                  noteId={note.id}
                  name={note.name}
                  image={note.image}
                  variant={index % 2 === 0 ? 'a' : 'b'}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
